import { Box, Flex, Grid, Heading, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { useState } from 'react';

import { tracking } from '#features/analytics';
import { GridGlowBackground } from '#features/landing/components/Landing/GridGlowBackground';
import { Button } from '#features/ui';
import {
  APPLY_URL,
  CAREERS_FAQ,
  DISCORD_URL,
  PROCESS_STEPS,
  QUICK_FACTS,
  ROLE_FILTERS,
  ROLES,
  SUPPORT_EMAIL,
  VALUE_POINTS,
  type Role,
  type RoleCategory,
} from '../../data';

const ACCENT = '#4B50E6';

// Subtly elevated dark band — a few % lighter than the base page, no hard edges.
// Matches the landing's SectionBand so careers reads as part of the same product.
const BAND_ELEVATED =
  'linear-gradient(180deg, hsla(228, 11.6%, 11.5%, 1) 0%, hsla(228, 11.6%, 9.5%, 1) 100%)';

const track = (action: string) => tracking.event({ action, category: 'careers_click' });

/** Full-bleed background band: background stretches edge-to-edge, content stays in the grid. */
const SectionBand = ({
  background,
  children,
}: {
  background: string;
  children: React.ReactNode;
}) => (
  <Box
    position="relative"
    w="100vw"
    ml="calc(50% - 50vw)"
    mr="calc(50% - 50vw)"
    background={background}
  >
    <Box
      position="absolute"
      inset={0}
      pointerEvents="none"
      background={`radial-gradient(ellipse 80% 60% at 50% 0%, ${ACCENT}14 0%, transparent 70%)`}
    />
    <Box
      position="relative"
      maxW={{ base: '640px', lg: '1200px' }}
      mx="auto"
      px={{ base: 4, md: 6, lg: 8 }}
    >
      {children}
    </Box>
  </Box>
);

const SectionHeader = ({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) => (
  <Box textAlign="center" mb={{ base: 8, lg: 10 }}>
    <Text
      textStyle="badge"
      color="purple.300"
      textTransform="uppercase"
      letterSpacing="0.15em"
      mb={3}
    >
      {eyebrow}
    </Text>
    <Heading
      as="h2"
      fontWeight="900"
      lineHeight="1.05"
      fontSize={{ base: '3xl', lg: '5xl' }}
      textTransform="uppercase"
      letterSpacing="-0.02em"
    >
      {title}
    </Heading>
    {subtitle && (
      <Text mt={4} textStyle={{ base: 'sm', md: 'md' }} color="fg.muted" maxW="620px" mx="auto">
        {subtitle}
      </Text>
    )}
  </Box>
);

const ExternalButton = ({
  href,
  action,
  children,
  ...rest
}: {
  href: string;
  action: string;
  children: React.ReactNode;
} & React.ComponentProps<typeof Button>) => (
  <Button asChild onClick={() => track(action)} {...rest}>
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  </Button>
);

const Hero = () => (
  <Stack
    gap={{ base: 6, lg: 7 }}
    alignItems={{ base: 'center', lg: 'flex-start' }}
    textAlign={{ base: 'center', lg: 'left' }}
    maxW="900px"
  >
    <Flex
      display="inline-flex"
      alignItems="center"
      gap={2}
      px={3}
      py={1.5}
      borderRadius="full"
      border="1px solid"
      borderColor="whiteAlpha.200"
      bg="whiteAlpha.50"
    >
      <Box w="7px" h="7px" borderRadius="full" bg={ACCENT} flexShrink={0} />
      <Text
        textStyle="badge"
        color="fg.emphasized"
        letterSpacing="0.08em"
        textTransform="uppercase"
      >
        Build with us
      </Text>
    </Flex>

    <Heading
      as="h1"
      fontWeight="900"
      fontSize={{ base: '4xl', md: '6xl', xl: '7xl' }}
      lineHeight="0.95"
      letterSpacing="-0.03em"
      textTransform="uppercase"
    >
      <Text as="span" display="block">
        Build the future of
      </Text>
      <Text
        as="span"
        display="block"
        style={{
          background: `linear-gradient(100deg, ${ACCENT} 0%, #8E7BFF 55%, ${ACCENT} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        playing together
      </Text>
    </Heading>

    <Text textStyle={{ base: 'md', md: 'lg' }} lineHeight="1.7" color="fg.muted" maxW="640px">
      Teamplay is a small crew of gamers building a better way to find people to play with. We are
      looking for like-minded people who want to build it with us – and grow with it.
    </Text>

    <Flex gap={3} flexDir={{ base: 'column', sm: 'row' }} w={{ base: '100%', sm: 'auto' }} mt={1}>
      <ExternalButton
        href={APPLY_URL}
        action="hero_apply"
        size="lg"
        minW="200px"
        w={{ base: '100%', sm: 'auto' }}
        style={{ boxShadow: `0 0 26px ${ACCENT}55` }}
      >
        Reach out
      </ExternalButton>
      <ExternalButton
        href={DISCORD_URL}
        action="hero_discord"
        size="lg"
        variant="outline"
        color="fg.emphasized"
        borderColor="whiteAlpha.200"
        w={{ base: '100%', sm: 'auto' }}
        _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
      >
        Say hi on Discord
      </ExternalButton>
      <Button
        asChild
        size="lg"
        variant="ghost"
        color="fg.muted"
        w={{ base: '100%', sm: 'auto' }}
        _hover={{ bg: 'whiteAlpha.50', color: 'fg.emphasized' }}
      >
        <a href="#roles">See where you fit</a>
      </Button>
    </Flex>

    <Flex gap={2.5} flexWrap="wrap" justifyContent={{ base: 'center', lg: 'flex-start' }} mt={1}>
      {QUICK_FACTS.map((fact) => (
        <Flex
          key={fact}
          alignItems="center"
          gap={2}
          px={3}
          py={2}
          borderRadius="full"
          border="1px solid"
          borderColor="whiteAlpha.100"
          bg="whiteAlpha.50"
        >
          <Box
            w="7px"
            h="7px"
            borderRadius="full"
            flexShrink={0}
            style={{ background: `linear-gradient(180deg, #8E7BFF 0%, ${ACCENT} 100%)` }}
          />
          <Text textStyle="title" color="fg.emphasized">
            {fact}
          </Text>
        </Flex>
      ))}
    </Flex>
  </Stack>
);

const ValuePoints = () => (
  <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={{ base: 4, md: 5 }}>
    {VALUE_POINTS.map((point) => (
      <Box
        key={point.title}
        p={6}
        borderRadius="2xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
      >
        <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
          {point.title}
        </Heading>
        <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
          {point.description}
        </Text>
      </Box>
    ))}
  </SimpleGrid>
);

const RoleCard = ({ role }: { role: Role }) => {
  const [open, setOpen] = useState(false);

  return (
    <Flex
      direction="column"
      h="100%"
      p={{ base: 5, md: 6 }}
      gap={4}
      borderRadius="2xl"
      border="1px solid"
      borderColor="whiteAlpha.100"
      background="linear-gradient(165deg, hsla(228, 11.6%, 14%, 0.85) 0%, hsla(228, 11.6%, 9%, 0.6) 100%)"
      transition="border-color 0.2s, transform 0.2s"
      _hover={{ borderColor: 'whiteAlpha.300', transform: 'translateY(-2px)' }}
    >
      <Text textStyle="micro" color="purple.300" textTransform="uppercase" letterSpacing="0.1em">
        {role.track}
      </Text>
      <Heading as="h3" fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" lineHeight="1.25">
        {role.title}
      </Heading>
      <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
        {role.summary}
      </Text>

      <Flex gap={2} flexWrap="wrap">
        {role.tags.map((tag) => (
          <Text
            key={tag}
            as="span"
            textStyle="2xs"
            fontWeight="600"
            color="fg.emphasized"
            px={2}
            py={1}
            borderRadius="full"
            bg="whiteAlpha.100"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            {tag}
          </Text>
        ))}
      </Flex>

      <Stack as="ul" gap={1.5} listStyleType="none" m={0} p={0}>
        {role.snippet.map((item) => (
          <Flex as="li" key={item} gap={2} alignItems="flex-start">
            <Box mt="7px" w="5px" h="5px" borderRadius="full" bg={ACCENT} flexShrink={0} />
            <Text textStyle="sm" color="fg.muted" lineHeight="1.5">
              {item}
            </Text>
          </Flex>
        ))}
      </Stack>

      {open && (
        <Stack gap={4} pt={2} borderTop="1px solid" borderColor="whiteAlpha.100">
          {role.detailBlocks.map((block) => (
            <Box key={block.label}>
              <Text
                textStyle="micro"
                color="cyan.300"
                textTransform="uppercase"
                letterSpacing="0.08em"
                mb={2}
              >
                {block.label}
              </Text>
              {block.text && (
                <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
                  {block.text}
                </Text>
              )}
              {block.list && (
                <Stack as="ul" gap={1.5} pl={4} m={0}>
                  {block.list.map((item) => (
                    <Text as="li" key={item} textStyle="sm" color="fg.muted" lineHeight="1.5">
                      {item}
                    </Text>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
          {role.note && (
            <Text
              textStyle="xs"
              color="fg.emphasized"
              p={3}
              borderRadius="lg"
              border="1px dashed"
              borderColor="whiteAlpha.200"
              bg="whiteAlpha.50"
            >
              {role.note}
            </Text>
          )}
        </Stack>
      )}

      <Flex gap={3} mt="auto" pt={1} flexWrap="wrap">
        <ExternalButton href={APPLY_URL} action={role.applyAction} size="md" flex="1" minW="150px">
          I'm interested
        </ExternalButton>
        <Button
          type="button"
          size="md"
          variant="outline"
          color="fg.emphasized"
          borderColor="whiteAlpha.200"
          onClick={() => setOpen((prev) => !prev)}
          _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
        >
          {open ? 'Hide details' : 'View details'}
        </Button>
      </Flex>
    </Flex>
  );
};

const OpenRoles = () => {
  const [filter, setFilter] = useState<RoleCategory | 'all'>('all');

  const visibleRoles = filter === 'all' ? ROLES : ROLES.filter((role) => role.id === filter);
  const countFor = (id: RoleCategory | 'all') =>
    id === 'all' ? ROLES.length : ROLES.filter((role) => role.id === id).length;

  return (
    <Box id="roles" css={{ scrollMarginTop: '80px' }}>
      <SectionHeader
        eyebrow="Open roles"
        title="Where you can jump in"
        subtitle="Pick what fits. Not sure? Just reach out – we shape roles around people."
      />

      <Flex gap={2.5} flexWrap="wrap" justifyContent="center" mb={{ base: 6, md: 8 }}>
        {ROLE_FILTERS.map((item) => {
          const active = filter === item.id;
          return (
            <Flex
              as="button"
              key={item.id}
              onClick={() => setFilter(item.id)}
              alignItems="center"
              gap={2}
              px={4}
              py={2}
              borderRadius="full"
              border="1px solid"
              borderColor={active ? 'transparent' : 'whiteAlpha.200'}
              bg={active ? undefined : 'whiteAlpha.50'}
              color={active ? 'white' : 'fg.muted'}
              textStyle="strong"
              cursor="pointer"
              transition="all 0.2s"
              style={
                active
                  ? { background: `linear-gradient(135deg, ${ACCENT} 0%, #8E7BFF 100%)` }
                  : undefined
              }
              _hover={
                active ? undefined : { borderColor: 'whiteAlpha.400', color: 'fg.emphasized' }
              }
            >
              {item.label}
              <Text
                as="span"
                textStyle="micro"
                minW="18px"
                textAlign="center"
                px={1.5}
                borderRadius="full"
                bg={active ? 'whiteAlpha.300' : 'whiteAlpha.100'}
              >
                {countFor(item.id)}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 4, md: 6 }} alignItems="stretch">
        {visibleRoles.map((role) => (
          <RoleCard key={role.id} role={role} />
        ))}
      </SimpleGrid>
    </Box>
  );
};

const HiringProcess = () => (
  <Box>
    <SectionHeader
      eyebrow="How it works"
      title="Getting started is simple"
      subtitle="No long process. A hello, a quick chat, and you are building."
    />
    <Grid
      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
      gap={{ base: 4, md: 5 }}
      maxW="900px"
      mx="auto"
    >
      {PROCESS_STEPS.map((item) => (
        <Box
          key={item.step}
          p={6}
          borderRadius="2xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
        >
          <Flex
            w="34px"
            h="34px"
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
            fontWeight="800"
            fontSize="md"
            color="white"
            mb={4}
            style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, #8E7BFF 100%)` }}
          >
            {item.step}
          </Flex>
          <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
            {item.title}
          </Heading>
          <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
            {item.description}
          </Text>
        </Box>
      ))}
    </Grid>
  </Box>
);

const FaqItem = ({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <Box
    border="1px solid"
    borderColor={isOpen ? 'whiteAlpha.200' : 'whiteAlpha.100'}
    borderRadius="xl"
    overflow="hidden"
    transition="border-color 0.2s"
  >
    <Flex
      asChild
      w="100%"
      px={6}
      py={5}
      alignItems="center"
      justifyContent="space-between"
      cursor="pointer"
      textAlign="left"
      bg={isOpen ? 'whiteAlpha.50' : 'transparent'}
      _hover={{ bg: 'whiteAlpha.50' }}
      transition="background 0.2s"
    >
      <button type="button" onClick={onToggle}>
        <Text fontWeight="700" textStyle={{ base: 'md', md: 'lg' }} pr={4}>
          {question}
        </Text>
        <Text
          textStyle="xl"
          color="purple.300"
          flexShrink={0}
          transition="transform 0.2s"
          style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          +
        </Text>
      </button>
    </Flex>
    {isOpen && (
      <Box px={6} pb={5}>
        <Text textStyle="sm" color="fg.muted" lineHeight="1.8">
          {answer}
        </Text>
      </Box>
    )}
  </Box>
);

const Faq = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <Box>
      <SectionHeader
        eyebrow="FAQ"
        title="Quick answers"
        subtitle="A few things before you reach out."
      />
      <Flex direction="column" gap={3} maxW="800px" mx="auto">
        {CAREERS_FAQ.map((item, index) => (
          <FaqItem
            key={item.question}
            question={item.question}
            answer={item.answer}
            isOpen={openIndex === index}
            onToggle={() => setOpenIndex((prev) => (prev === index ? null : index))}
          />
        ))}
      </Flex>
    </Box>
  );
};

const FinalCta = () => (
  <Box position="relative" w="100%">
    <Box
      position="absolute"
      top="-70px"
      left="50%"
      transform="translateX(-50%)"
      w="360px"
      h="360px"
      background={`radial-gradient(circle, ${ACCENT}26 0%, transparent 70%)`}
      pointerEvents="none"
    />
    <Flex
      direction="column"
      alignItems="center"
      gap={5}
      py={{ base: 12, md: 16 }}
      px={{ base: 6, md: 12 }}
      background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.85) 0%, hsla(228, 11.6%, 9%, 0.5) 100%)"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="3xl"
      textAlign="center"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top={0}
        left="10%"
        right="10%"
        h="1px"
        background={`linear-gradient(90deg, transparent, ${ACCENT}88, transparent)`}
      />
      <Heading
        as="h2"
        fontSize={{ base: '3xl', md: '5xl' }}
        lineHeight="1.02"
        maxW="620px"
        fontWeight="900"
        textTransform="uppercase"
        letterSpacing="-0.02em"
      >
        Want in? Let&apos;s build it.
      </Heading>
      <Text textStyle="md" color="fg.muted" maxW="600px">
        If you are a gamer who wants to build something real with people who get it, say hi. We will
        take it from there.
      </Text>
      <Flex gap={3} flexDir={{ base: 'column', sm: 'row' }} w={{ base: '100%', sm: 'auto' }} mt={2}>
        <ExternalButton
          href={APPLY_URL}
          action="footer_apply"
          size="lg"
          minW="220px"
          w={{ base: '100%', sm: 'auto' }}
          style={{ boxShadow: `0 0 28px ${ACCENT}59` }}
        >
          Reach out
        </ExternalButton>
        <ExternalButton
          href={DISCORD_URL}
          action="footer_discord"
          size="lg"
          variant="outline"
          color="fg.emphasized"
          borderColor="whiteAlpha.200"
          w={{ base: '100%', sm: 'auto' }}
          _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
        >
          Join our Discord
        </ExternalButton>
      </Flex>
      <Text textStyle="sm" color="fg.subtle" mt={1}>
        Prefer email? Reach us at{' '}
        <Text asChild color="purple.300" fontWeight="600">
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </Text>
      </Text>
    </Flex>
  </Box>
);

export const BuildWithUs = () => (
  <Stack
    position="relative"
    m="0 auto"
    gap={0}
    w="100%"
    maxW={{ base: '640px', lg: '1200px' }}
    px={{ base: 4, md: 6, lg: 8 }}
    mb={4}
    // Landing-scoped contrast + accent overrides (same trick as Landing): brighten
    // muted/subtle foreground for AA on dark, and tune the purple accent tokens so
    // text-on-dark reads brighter while solid buttons keep white labels legible.
    css={{
      '--chakra-colors-fg-muted': 'rgba(255, 255, 255, 0.55)',
      '--chakra-colors-fg-subtle': 'rgba(255, 255, 255, 0.5)',
      '--chakra-colors-purple-300': '#8288f5',
      '--chakra-colors-purple-200': '#8288f5',
      '--chakra-colors-purple-fg': '#8288f5',
      '--chakra-colors-purple-solid': '#5158e2',
    }}
  >
    {/* Hero — full-bleed animated grid-glow background, content stays in the 1200px grid. */}
    <Box
      position="relative"
      w="100vw"
      ml="calc(50% - 50vw)"
      mr="calc(50% - 50vw)"
      mt={{ base: -5, lg: -5 }}
    >
      <GridGlowBackground>
        <Box
          maxW={{ base: '640px', lg: '1200px' }}
          mx="auto"
          px={{ base: 4, md: 6, lg: 8 }}
          py={{ base: 12, lg: 20 }}
        >
          <Hero />
        </Box>
      </GridGlowBackground>
    </Box>

    {/* Why join — elevated colour band */}
    <SectionBand background={BAND_ELEVATED}>
      <Box py={{ base: 12, md: 16 }}>
        <SectionHeader
          eyebrow="Who we look for"
          title="Like-minded builders"
          subtitle="Gamers who want to build something real and stick around for the ride."
        />
        <ValuePoints />
      </Box>
    </SectionBand>

    {/* Open roles — base */}
    <Box py={{ base: 12, md: 16 }}>
      <OpenRoles />
    </Box>

    {/* Hiring process — elevated colour band */}
    <SectionBand background={BAND_ELEVATED}>
      <Box py={{ base: 12, md: 16 }}>
        <HiringProcess />
      </Box>
    </SectionBand>

    {/* FAQ — base */}
    <Box py={{ base: 12, md: 16 }}>
      <Faq />
    </Box>

    {/* Final CTA — base */}
    <Box py={{ base: 12, md: 16 }}>
      <FinalCta />
    </Box>
  </Stack>
);
